import { OptionItem } from "@/shared/types"
import { ZodEnum } from "zod"

/**
 * Base parameters for all schema configurations
 */
type SchemaBaseParams = {
    /**
     * Determines if the field is required in the schema
     * @default true
     */
    required?: boolean,
}

type SchemaBaseOptions = {
    /**
     * Determines if the field is required in the schema
     * @default true
     */
    required?: boolean,
}

type TextSchemaOptions = SchemaBaseOptions & {
    size?: TextSchemaSize,
    minLength?: number,
    maxLength?: number,
}

type TextAreaSchemaOptions = SchemaBaseOptions & {
    minLength?: number,
    maxLength?: number,
}

type ArraySchemaOptions = SchemaBaseOptions & {
    options: readonly OptionItem[],
}

type BooleanSchemaOptions = SchemaBaseOptions

type NumberSchemaOptions = SchemaBaseOptions & {
    min?: number,
    max?: number,
}





/**
 * Size options for string schema validation
 * Each size represents a maximum character length limit
 */
export type TextSchemaSize = 
    /** Maximum 50 characters - For short inputs like names, titles */
    | "sm" 
    /** Maximum 200 characters - For medium inputs like addresses */
    | "md" 
    /** Maximum 1000 characters - For long form content */
    | "lg"

/**
 * Enumeration of all supported schema field types
 */
export enum SchemaType {
    /** General text input field */
    text = "text",
    /** Email input with email format validation */
    email = "email",
    /** Phone number input with phone format validation */
    phone = "phone",
    /** Date input field */
    date = "date",
    /** Password input with password requirements validation */
    password = "password",
    /** Boolean input for true/false values */
    boolean = "boolean",
    /** Numeric input with optional min/max validation */
    number = "number",
    /** Text area input with optional min/max validation */
    textArea = "textArea",
    /** Array input with optional min/max length validation */
    array = "array",
}

export type EmailSchemaTypeConfig = {
    type: SchemaType.email,
}

export type PhoneSchemaTypeConfig = {
    type: SchemaType.phone,
}

export type TextSchemaTypeConfig = {
    type: SchemaType.text,
    /** Minimum length requirement for text */
    minLength?: number,
    /** Maximum length requirement for text */
    maxLength?: number,
}

export type TextAreaSchemaTypeConfig = {
    type: SchemaType.textArea,
    /** Minimum length requirement for text area */
    minLength?: number,
    /** Maximum length requirement for text area */
    maxLength?: number,
}

export type ArraySchemaTypeConfig = {
    type: SchemaType.array,
    /** Minimum length requirement for array */
    min?: number,
    /** Maximum length requirement for array */
    max?: number,
}

export type BooleanSchemaTypeConfig = {
    type: SchemaType.boolean,
}

export type NumberSchemaTypeConfig = {
    type: SchemaType.number,
    /** Minimum allowed value */
    min?: number,
    /** Maximum allowed value */
    max?: number,
}


/**
 * Union type defining configuration options for different schema types
 */
export type SchemaTypeConfig =
    /** Text field configuration */
    | {
        type: SchemaType.text,
        /** Minimum length requirement for text */
        minLength?: number,
        /** Maximum length requirement for text */
        maxLength?: number,
        /** Predefined size constraint (sm/md/lg) */
        size?: TextSchemaSize,
    }
    /** Email field configuration */
    | {
        type: SchemaType.email,
    }
    /** Phone field configuration */
    | {
        type: SchemaType.phone,
    }
    /** Date field configuration */
    | {
        type: SchemaType.date,
    }
    /** Password field configuration */
    | {
        type: SchemaType.password,
    }
    /** Boolean field configuration */
    | {
        type: SchemaType.boolean,
    }
    /** Number field configuration */
    | {
        type: SchemaType.number,
        /** Minimum allowed value */
        min?: number,
        /** Maximum allowed value */
        max?: number,
        /** Minimum length requirement for text */
        minLength?: number,
        /** Maximum length requirement for text */
        maxLength?: number,
    } | {
        type: SchemaType.textArea,
        /** Minimum length requirement for text area */
        minLength?: number,
        /** Maximum length requirement for text area */
        maxLength?: number,
    } | {
        type: SchemaType.array,
        /** Minimum length requirement for array */
        min?: number,
        /** Maximum length requirement for array */
        max?: number,
        /** Options for the array */
        options: readonly OptionItem[],
    }
/**
 * Combined type for creating string schemas
 * Merges base parameters with type-specific configuration
 */
export type CreateTextSchemaParams = SchemaBaseParams & SchemaTypeConfig
